/**
 * Row-level validation for the Agent Submit Leads Excel importer.
 * Pure functions — no side effects, no DB calls.
 */
import { MEDICAL_CONCERN_VALUES } from '@/shared/constants/lead-enums';

export interface SubmitLeadRow {
  name: string;
  date_of_birth: string;
  gender: string;
  nationality: string;
  phone: string;
  email: string;
  medical_concern: string;
  arrival_date: string;
  departure_date: string;
  accompanying_persons: string;
  notes: string;
  passport_number: string;
  passport_expiry: string;
}

export type RowErrors = Partial<Record<keyof SubmitLeadRow, string>>;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const E164_RE = /^\+[1-9]\d{6,14}$/;

/** Matches the value format the Excel template/import uses. */
export const DEFAULT_NATIONALITY = 'Cambodia';

export function emptyRow(): SubmitLeadRow {
  return {
    name: '', date_of_birth: '', gender: '', nationality: DEFAULT_NATIONALITY,
    phone: '+855 ', email: '', medical_concern: '',
    arrival_date: '', departure_date: '', accompanying_persons: '', notes: '',
    passport_number: '', passport_expiry: '',
  };
}

/**
 * Soft, non-blocking passport warnings (advisory only — never fail validation).
 * - number present but expiry missing
 * - expiry in the past
 * - expiry within 6 months of the arrival date
 * - expiry present but not strict YYYY-MM-DD
 */
export function passportWarnings(row: Pick<SubmitLeadRow, 'passport_number' | 'passport_expiry' | 'arrival_date'>): string[] {
  const out: string[] = [];
  const num = row.passport_number?.trim();
  const exp = row.passport_expiry?.trim();
  if (num && !exp) out.push('Passport number provided but expiry is missing');
  if (exp) {
    if (!DATE_RE.test(exp) || Number.isNaN(new Date(exp + 'T00:00:00').getTime())) {
      out.push('Passport Expiry must be YYYY-MM-DD');
    } else {
      const expiry = new Date(exp + 'T00:00:00');
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (expiry < today) {
        out.push('Passport expiry is in the past');
      } else if (row.arrival_date && DATE_RE.test(row.arrival_date)) {
        const sixMonthsAfterArrival = new Date(row.arrival_date + 'T00:00:00');
        sixMonthsAfterArrival.setMonth(sixMonthsAfterArrival.getMonth() + 6);
        if (expiry < sixMonthsAfterArrival) {
          out.push('Passport expires within 6 months of arrival date');
        }
      }
    }
  }
  return out;
}

export function validateRow(
  row: SubmitLeadRow,
  allowedConcerns: string[] = MEDICAL_CONCERN_VALUES as unknown as string[],
): RowErrors {
  const errs: RowErrors = {};
  if (!row.name?.trim()) errs.name = 'Required';
  if (!row.nationality?.trim()) errs.nationality = 'Required';
  if (!row.phone?.trim()) {
    errs.phone = 'Required';
  } else if (!E164_RE.test(row.phone.trim())) {
    errs.phone = 'Use E.164 (+855…, +84…)';
  }
  if (!row.medical_concern?.trim()) {
    errs.medical_concern = 'Required';
  } else if (allowedConcerns.length && !allowedConcerns.includes(row.medical_concern.trim())) {
    errs.medical_concern = 'Not in allowed list';
  }
  if (!row.date_of_birth?.trim()) {
    errs.date_of_birth = 'Required';
  } else if (!DATE_RE.test(row.date_of_birth)) {
    errs.date_of_birth = 'Use YYYY-MM-DD';
  } else {
    const d = new Date(row.date_of_birth + 'T00:00:00');
    if (Number.isNaN(d.getTime())) errs.date_of_birth = 'Invalid date';
    else if (d.getTime() > Date.now()) errs.date_of_birth = 'Cannot be in the future';
    else if (d.getFullYear() < 1900) errs.date_of_birth = 'Must be 1900 or later';
  }
  if (row.arrival_date && !DATE_RE.test(row.arrival_date)) errs.arrival_date = 'Use YYYY-MM-DD';
  if (row.departure_date && !DATE_RE.test(row.departure_date)) errs.departure_date = 'Use YYYY-MM-DD';
  if (row.passport_expiry && !DATE_RE.test(row.passport_expiry)) errs.passport_expiry = 'Use YYYY-MM-DD';
  if (row.arrival_date && row.departure_date && DATE_RE.test(row.arrival_date) && DATE_RE.test(row.departure_date)) {
    if (row.departure_date < row.arrival_date) errs.departure_date = 'Must be ≥ Arrival';
  }
  return errs;
}

export function rowHasErrors(errs: RowErrors): boolean {
  return Object.keys(errs).length > 0;
}

export function isRowEmpty(row: SubmitLeadRow): boolean {
  // Treat a bare "+855" / "+855 " default prefix as empty so newly-added rows
  // (which pre-fill the Cambodia country code) don't trigger validation.
  const phoneStripped = String(row.phone ?? '').replace(/^\+855\s*$/, '').trim();
  if (phoneStripped) return false;
  return Object.entries(row).every(([k, v]) => {
    if (k === 'phone') return true;
    if (k === 'nationality' && String(v ?? '').trim() === DEFAULT_NATIONALITY) return true;
    return !String(v ?? '').trim();
  });
}

export const MAX_ROWS = 50;

const DRAFT_KEY = 'agent_submit_leads_draft';
/** sessionStorage only — patient data on shared partner devices. */
export function saveSubmitLeadsDraft(rows: SubmitLeadRow[]) {
  try {
    if (rows.every(isRowEmpty)) sessionStorage.removeItem(DRAFT_KEY);
    else sessionStorage.setItem(DRAFT_KEY, JSON.stringify(rows));
  } catch { /* storage unavailable */ }
}
export function loadSubmitLeadsDraft(): SubmitLeadRow[] | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || !parsed.length) return null;
    return parsed.slice(0, MAX_ROWS).map((r: Partial<SubmitLeadRow>) => ({ ...emptyRow(), ...r }));
  } catch { return null; }
}
export function clearSubmitLeadsDraft() {
  try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* storage unavailable */ }
}
